import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import shareIcon from "../../../assets/images/share.png";
import closeIcon from "../../../assets/images/close.png";
import arrowIconWhite from "../../../assets/images/arrowIconWhite.png";

import Img01 from "../../../assets/images/loghi 2x/sezione-05-popup-07-GPTW.png";
import Img02 from "../../../assets/images/loghi 2x/sezione-05-popup-07-Rina.png";
import Img03 from "../../../assets/images/logo_wellbeing.webp";

function Card({ children, img, imgAlt = "", report, href }) {
  const hasImg = Boolean(img);

  return (
    <div className="bg-white rounded-[28px] border border-[#0E3761]/10 p-6 md:p-8">
      <div className="flex flex-col gap-4 h-full items-start">

        {/* Logo al posto del titolo */}
        {hasImg && (
          <img
            src={img}
            alt={imgAlt || ""}
            className="h-[110px] object-contain"
            loading="lazy"
          />
        )}

        {children}

        <div className="pt-2 mt-auto">
          <a href={href} target="_blank" rel="noopener">
              <button
                className="bt-secondary2"
              >
                <img
                  src={arrowIconWhite}
                  alt="Arrow"
                  className="inline-block mr-2 w-[20px] h-[20px]"
                />
                {report || "Consult the Report"}
              </button>
            </a>
        </div>
      </div>
    </div>
  );
}


export default function Care({ isOpen, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onShare = (popupId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("popup", popupId);

    if (navigator.share) {
      navigator
        .share({ title: "Taking care of us", url: url.toString() })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).catch(() => {});
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[1400] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3 md:p-6"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Taking care of us"
    >
      <div
        className="relative w-full md:w-[1280px] bg-white max-h-[90vh] overflow-y-auto overscroll-contain"
        style={{ background: "#E7E3DF", WebkitOverflowScrolling: "touch" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-3 z-10 flex items-center gap-2 justify-end pr-3">
          <button
            onClick={() => onShare("Care")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Share"
            aria-label="Share"
          >
            <img src={shareIcon} alt="" className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Close"
            aria-label="Close"
          >
            <img src={closeIcon} alt="Close" className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          <h2 className="text-[#5188A1] text-[30px] md:text-[40px] font-bold">
            Taking care of us
          </h2>

          {/* Griglia card: 1 col mobile, 2 col desktop */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Card 1 */}
            <Card img={Img01} imgAlt="Great Place To Work" report="Consult the Impact Report - chapter 7.4" href="https://www.realegroup.eu/groupshare/0/EN/ListaReportsAndAccounts/64/Reale%20Group_Integrated%20Consolidated%20Financial%20Statement%202025.pdf#page=388">
              <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                In 2025, Reale Group once again obtained the Great Place to Work®
                certification—a key milestone in the Group’s People Strategy and an
                important listening channel to keep the company’s Purpose tangible
                and alive.
              </p>
            </Card>

            {/* Card 2 */}
            <Card img={Img02} imgAlt="RINA Gender Equality" report="Consult the Report - chapter 3 • par. 3.2.4" href="https://www.realegroup.eu/groupshare/0/EN/ListaReportsAndAccounts/64/Reale%20Group_Integrated%20Consolidated%20Financial%20Statement%202025.pdf#page=167">
              <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                In 2025, a dedicated unit for Diversity, Equity and Inclusion was
                established.
              </p>
              <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                Reale Mutua, Italiana Assicurazioni, and Blue Assistance obtained
                Gender Equality Certification, in accordance with the UNI/PdR
                125:2022 standard. The goal is to progressively extend this
                certification to all companies within the Group.
              </p>
            </Card>

            {/* Card 3 – full width */}
            <div className="md:col-span-2">
              <Card img={Img03} imgAlt="Reale Wellbeing" report="Consult the Report - chapter 3 • par. 3.2.4" href="https://www.realegroup.eu/groupshare/0/EN/ListaReportsAndAccounts/64/Reale%20Group_Integrated%20Consolidated%20Financial%20Statement%202025.pdf#page=167">
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Introduction of the Family Manager service, which supports
                  parents in accessing concrete resources (study guidance,
                  babysitting, study holidays).
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Financial top-ups for parental leave and an increase in paid
                  paternity days.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Solidarity Time Bank, to foster solidarity among colleagues who
                  need to take time off for caregiving purposes.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  For people undertaking protection pathways following situations of
                  violence, INPS-paid leave has been extended to 180 days over a
                  three-year period.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Wellbeing EDU programme, which includes webinars and workshops
                  (such as those run with Slow Food) to promote sustainable and
                  healthy food choices.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Redesign of spaces (New Ways of Working project) to make them
                  enablers of collaboration and wellbeing.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Redesign of the food offering at our offices, including the
                  installation of new vending machines with healthy food and a snack
                  selection suitable for people with specific dietary needs.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
